import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import './EmailVerificationModal.css';

const EmailVerificationModal = ({ isOpen, onClose, onVerified }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  const handleSendOTP = async () => {
    setSending(true);
    try {
      const response = await api.post('/auth/send-email-verification');
      toast.success(response.message || 'OTP sent to your email');
      setOtpSent(true);
      setCanResend(false);
      setTimer(60); // 1 minute timer
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  // Timer countdown effect
  React.useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/verify-email', { otp });
      toast.success(response.message || 'Email verified successfully');

      // Update user in localStorage
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (storedUser) {
        storedUser.isEmailVerified = true;
        localStorage.setItem('user', JSON.stringify(storedUser));
      }

      if (onVerified) onVerified();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content email-verification-modal">
        <div className="modal-header">
          <h2>
            <i className="fas fa-envelope-circle-check"></i>
            Verify Your Email
          </h2>
          <p>Email verification is required to access My Jobs and Applications</p>
        </div>

        <form onSubmit={handleVerify} className="verification-form">
          <div className="info-box">
            <i className="fas fa-info-circle"></i>
            <p>We'll send a 6-digit OTP to your registered email address</p>
          </div>

          <button
            type="button"
            className="btn btn-secondary btn-send-otp"
            onClick={handleSendOTP}
            disabled={sending || !canResend}
          >
            {sending ? (
              <>
                <span className="spinner-small"></span>
                Sending...
              </>
            ) : !canResend ? (
              <>
                <i className="fas fa-clock"></i>
                Resend in {timer}s
              </>
            ) : otpSent ? (
              <>
                <i className="fas fa-redo"></i>
                Resend OTP
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                Send OTP to Email
              </>
            )}
          </button>

          <div className="form-group">
            <label htmlFor="otp">Enter OTP</label>
            <input
              type="text"
              id="otp"
              className="form-control otp-input"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              maxLength={6}
              required
            />
          </div>

          <div className="modal-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  Verifying...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  Verify Email
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
